import PricingModelEditor from "./PricingModelEditor";
import {
  AddRounded as AddIcon,
  DeleteRounded as DeleteIcon,
  EditRounded as EditIcon,
} from "@mui/icons-material";
import {
  Alert,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  Paper,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { makeNewId } from "common/ids";
import Component from "components/Component";
import RawConfigEditor from "components/admin/RawConfigEditor";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import * as config from "config";
import { useState, useEffect, useCallback, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  useLazyGetStaticDataQuery,
  useUpdateStaticDataMutation,
} from "services/adminService";
import * as utils from "utils";

const workbookVersion = (model) =>
  model?.workbook?.model_path?.split("/")?.[2] ?? "[none]";

const DisplayPricingModel = ({ model, onEdit, onDelete, onToggleActive }) => {
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [reason, setReason] = useState("");

  const handleConfirmDelete = () => {
    onDelete({ model, userLogMessage: reason });
    setConfirmDelete(false);
  };

  return (
    <>
      <Dialog
        open={confirmDelete}
        onClose={() => setConfirmDelete(false)}
        aria-labelledby={"alert-dialog-title"}
        aria-describedby={"alert-dialog-description"}
      >
        <DialogTitle id={"alert-dialog-title"}>
          {"Delete Pricing Model?"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} alignItems={"center"}>
            <Grid item xs={12}>
              <DialogContentText id={"alert-dialog-description"}>
                {`Are you sure you want to delete the pricing model "${model.title}"? This action cannot be undone.`}
              </DialogContentText>
            </Grid>
            <Grid item xs={12}>
              <TextField
                type={"text"}
                InputLabelProps={{ shrink: true }}
                InputProps={{ spellCheck: "false" }}
                label={"Reason for change"}
                autoComplete={"off"}
                variant={"filled"}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)} autoFocus>
            {"Cancel"}
          </Button>
          <Button onClick={handleConfirmDelete} isDisabled={reason?.length < 4}>
            {"Delete"}
          </Button>
        </DialogActions>
      </Dialog>
      <Paper variant={"outlined"} sx={{ padding: "0.5rem 0.625rem" }}>
        <Grid container spacing={2} alignItems={"center"}>
          <Grid item xs={4}>
            <Typography>{model.title}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Typography sx={{ fontFamily: "monospace" }}>
              {model.engine || "[unset]"}
            </Typography>
          </Grid>
          <Grid item xs={2}>
            <Typography>{workbookVersion(model)}</Typography>
          </Grid>
          <Grid item xs={3}>
            <Stack
              direction={"row"}
              justifyContent={"flex-end"}
              alignItems={"center"}
            >
              <IconButton
                onClick={onEdit}
                icon={EditIcon}
                variant={"bright"}
                tooltip={"Edit pricing model"}
              />
              <IconButton
                onClick={() => setConfirmDelete(true)}
                icon={DeleteIcon}
                variant={"bright"}
                tooltip={"Delete pricing model"}
              />
              <Tooltip
                title={
                  model.active ?? true
                    ? "Deactivate — removes the model's tab for client users on save"
                    : "Activate — gives client users the model's tab on save"
                }
              >
                <Switch
                  checked={model.active ?? true}
                  onChange={() => onToggleActive(model)}
                  inputProps={{ "aria-label": "Active" }}
                />
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>
      </Paper>
    </>
  );
};

const ColumnHeadings = () => (
  <Grid container spacing={2} sx={{ padding: "0 0.625rem" }}>
    <Grid item xs={4}>
      <Typography variant={"caption"} color={"text.secondary"}>
        {"Title"}
      </Typography>
    </Grid>
    <Grid item xs={3}>
      <Typography variant={"caption"} color={"text.secondary"}>
        {"Engine"}
      </Typography>
    </Grid>
    <Grid item xs={2}>
      <Typography variant={"caption"} color={"text.secondary"}>
        {"Workbook Version"}
      </Typography>
    </Grid>
    <Grid item xs={3}>
      <Typography
        variant={"caption"}
        color={"text.secondary"}
        align={"right"}
        display={"block"}
      >
        {"Active"}
      </Typography>
    </Grid>
  </Grid>
);

const updateEntry = (data, entry) => {
  if (entry.id == null) {
    return [
      ...(data ?? []),
      {
        ...entry,
        id: makeNewId(),
      },
    ];
  }
  return (data ?? []).map((e) => (e.id === entry.id ? entry : e));
};

const deleteEntry = (data, entry) => {
  return (data ?? []).filter((e) => e.id !== entry.id);
};

const PricingModels = () => {
  const { companyId } = useParams();

  const [loadModels, { data: models, isLoading }] = useLazyGetStaticDataQuery();

  const modelsList = useMemo(() => (Array.isArray(models) ? models : []), [
    models,
  ]);

  // Staged Active changes: null follows the server, otherwise these are edits awaiting Save.
  const [draft, setDraft] = useState(null);
  useEffect(() => setDraft(null), [models]);
  const editedList = draft ?? modelsList;
  const isDirty = draft != null;

  const sortedModels = useMemo(
    () =>
      [...editedList].sort((a, b) =>
        (a?.title || "").localeCompare(b?.title || "")
      ),
    [editedList]
  );

  const [update, { isLoading: isUpdating }] = useUpdateStaticDataMutation();
  const [activeModel, setActiveModel] = useState(null);
  const [newModelOpen, setNewModelOpen] = useState(false);
  const [reason, setReason] = useState("");
  const refresh = useCallback(() => {
    loadModels({ companyId, key: "pricing_models" });
    setActiveModel(null);
  }, [loadModels, setActiveModel, companyId]);
  const pollForChanges = useCallback(async () => {
    const response = await utils.authenticatedFetch(
      config.endpoints.admin(`static-data/${companyId}/pricing_models`),
      {}
    );
    return await response.json();
  }, [companyId]);
  // .then() fires even when the mutation rejects, so a failed write used to close the dialog and
  // discard the editor's contents as if it had saved. unwrap() makes the rejection observable.
  const [saveError, setSaveError] = useState(null);
  const write = async (data, userLogMessage, onDone) => {
    setSaveError(null);
    try {
      await update({
        companyId,
        key: "pricing_models",
        data,
        userLogMessage,
      }).unwrap();
    } catch (error) {
      setSaveError(
        error?.description ??
          error?.message ??
          "Could not save the pricing models."
      );
      return;
    }
    onDone();
  };

  const onSave = ({ model, userLogMessage }) =>
    write(updateEntry(modelsList, model), userLogMessage, () => {
      setNewModelOpen(false);
      refresh();
    });

  const onDelete = ({ model, userLogMessage }) =>
    write(deleteEntry(modelsList, model), userLogMessage, refresh);

  const onToggleActive = (model) =>
    setDraft((current) =>
      (current ?? modelsList).map((entry) =>
        entry.id === model.id
          ? { ...entry, active: !(entry.active ?? true) }
          : entry
      )
    );

  const onSaveActive = () =>
    write(editedList, reason, () => {
      setDraft(null);
      setReason("");
      refresh();
    });

  const onResetActive = () => {
    setDraft(null);
    setReason("");
  };
  useEffect(() => {
    if (companyId != null) {
      refresh();
    }
  }, [companyId, refresh]);

  return (
    <Component
      title={"Pricing Models"}
      loading={isLoading || isUpdating}
      options={
        <Button startIcon={<AddIcon />} onClick={() => setNewModelOpen(true)}>
          {"Add Pricing Model"}
        </Button>
      }
    >
      {companyId && (
        <RawConfigEditor
          data={models}
          onSave={async (parsedJson, logMessage) => {
            await update({
              companyId,
              key: "pricing_models",
              data: parsedJson,
              userLogMessage: logMessage,
            });
            refresh();
          }}
          isSaving={isUpdating}
          pollForChanges={pollForChanges}
        >
          {saveError && (
            <Grid item xs={12}>
              <Alert severity={"error"}>{saveError}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              type={"text"}
              InputLabelProps={{ shrink: true }}
              InputProps={{ spellCheck: "false" }}
              label={"Reason for change"}
              autoComplete={"off"}
              variant={"filled"}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              fullWidth
            />
          </Grid>
          {sortedModels.length > 0 && (
            <Grid item xs={12}>
              <ColumnHeadings />
            </Grid>
          )}
          {sortedModels.map((model) => (
            <Grid item xs={12} key={model.id}>
              <DisplayPricingModel
                model={model}
                onEdit={() => setActiveModel(model)}
                onDelete={onDelete}
                onToggleActive={onToggleActive}
              />
            </Grid>
          ))}
          {sortedModels.length === 0 && (
            <Grid item xs={12}>
              <Stack
                sx={{ width: "100%", padding: "3rem 0" }}
                alignItems={"center"}
                justifyContent={"center"}
              >
                <Typography color={"lightslategrey !important"}>
                  {"No pricing models configured at present"}
                </Typography>
              </Stack>
            </Grid>
          )}
          {sortedModels.length > 0 && (
            <Grid item xs={12}>
              <Stack
                direction={"row"}
                justifyContent={"flex-end"}
                alignItems={"center"}
                spacing={2}
                sx={{ mt: 1 }}
              >
                {isDirty && (
                  <Typography variant={"caption"} color={"warning.main"}>
                    {"Unsaved changes"}
                  </Typography>
                )}
                <Button
                  color={"secondary"}
                  onClick={onResetActive}
                  isDisabled={!isDirty || isUpdating}
                >
                  {"Reset"}
                </Button>
                <Tooltip
                  title={
                    !isDirty
                      ? "Toggle a model's Active switch first"
                      : reason.trim().length < 4
                      ? "Enter a reason for change before saving"
                      : ""
                  }
                >
                  <span>
                    <Button
                      onClick={onSaveActive}
                      isDisabled={
                        !isDirty || reason.trim().length < 4 || isUpdating
                      }
                    >
                      {"Save"}
                    </Button>
                  </span>
                </Tooltip>
              </Stack>
            </Grid>
          )}
          <Grid item xs={12}>
            {activeModel?.id != null && (
              <PricingModelEditor
                isOpen={true}
                onClose={() => setActiveModel(null)}
                model={activeModel}
                onSave={onSave}
                companyId={companyId}
                reason={reason}
                setReason={setReason}
              />
            )}
          </Grid>
          <Grid item xs={12}>
            {newModelOpen && (
              <PricingModelEditor
                isOpen={true}
                onClose={() => setNewModelOpen(false)}
                model={{
                  title: "New Pricing Model",
                  engine: "",
                  active: true,
                  metrics: {},
                  fields: [],
                  resultLabels: {},
                }}
                companyId={companyId}
                onSave={onSave}
                reason={reason}
                setReason={setReason}
              />
            )}
          </Grid>
        </RawConfigEditor>
      )}
    </Component>
  );
};

export default PricingModels;
